import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Users, BarChart3, Download, UserPlus, CreditCard as Edit, Trash2, Eye, EyeOff, Save, X, Phone, FileSpreadsheet, UserX, ChefHat, Upload, Loader as Loader2, Gift, Trophy, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { SupabaseAuth } from '../utils/supabaseAuth';
import { ContactsManager } from '../components/ContactsManager';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import {
  exportEquipamentosToExcel,
  exportReservasSalasToExcel,
  exportAgendamentosPortariaToExcel,
  exportReservasVeiculosToExcel,
  exportTrocasProteinasToExcel
} from '../utils/excelExports';

interface User {
  id: string;
  auth_id: string;
  nome: string;
  email: string;
  setor: string;
  role: string;
  ativo: boolean;
  created_at: string;
}

interface SystemStats {
  usuarios_ativos: number;
  posts_mural: number;
  reservas_salas: number;
  solicitacoes_ti: number;
  trocas_proteina: number;
  agendamentos_portaria: number;
}

interface Contact {
  id: string;
  nome: string;
  cargo: string;
  setor: string;
  email: string;
  telefone: string;
  ramal: string;
  localizacao: string;
  tipo: string;
  ativo: boolean;
  created_at: string;
}

interface Vencedor {
  usuario_id: string;
  usuario: { nome: string };
}

interface Brinde {
  id: string;
  titulo: string;
  descricao: string;
  imagem_url: string | null;
  data_sorteio: string;
  quantidade: number;
  status: 'ativo' | 'sorteado' | 'cancelado';
  vencedor_id: string | null;
  criado_por: string;
  criado_em: string;
  sorteado_em: string | null;
  vencedor?: { nome: string };
  vencedores?: Vencedor[];
  total_inscricoes?: number;
}

interface NovoBrindeState {
  titulo: string;
  descricao: string;
  imagem_url: string;
  data_sorteio: string;
  quantidade: number;
}

export const AdminPanel: React.FC = () => {
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin';
  const isRH = user?.setor === 'RH' || user?.role === 'rh';
  const isSorteioManager = user?.role === 'sorteio' || user?.role === 'moderador';

  const canManageUsers = isAdmin || isRH;
  const canManageContacts = isAdmin || isRH;
  const canAccessReports = isAdmin || isRH;
  const canManageSorteios = isAdmin || isSorteioManager;

  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'contacts' | 'cardapio' | 'brindes' | 'reports'>('dashboard');
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [newUser, setNewUser] = useState({
    nome: '',
    email: '',
    senha: '',
    setor: 'Geral',
    role: 'colaborador'
  });

  const [uploadingCardapio, setUploadingCardapio] = useState(false);
  const [cardapioFile, setCardapioFile] = useState<File | null>(null);

  const [brindes, setBrindes] = useState<Brinde[]>([]);
  const [showNovoBrindeModal, setShowNovoBrindeModal] = useState(false);
  const [novoBrinde, setNovoBrinde] = useState<NovoBrindeState>({
    titulo: '',
    descricao: '',
    imagem_url: '',
    data_sorteio: '',
    quantidade: 1, // ✅ default 1
  });
  const [sorteandoBrindeId, setSorteandoBrindeId] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadSystemStats();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'brindes') {
      loadBrindes();
    }
  }, [activeTab]);

  const loadSystemStats = async () => {
    try {
      setLoading(true);

      const [usersResult, postsResult, reservasResult, tiResult, proteinaResult, portariaResult] = await Promise.all([
        supabase.from('usuarios').select('id', { count: 'exact', head: true }).eq('ativo', true),
        supabase.from('mural_posts').select('id', { count: 'exact', head: true }),
        supabase.from('reservas_salas').select('id', { count: 'exact', head: true }),
        supabase.from('ti_solicitacoes').select('id', { count: 'exact', head: true }),
        supabase.from('trocas_proteina').select('id', { count: 'exact', head: true }),
        supabase.from('portaria_agendamentos').select('id', { count: 'exact', head: true }),
      ]);

      setStats({
        usuarios_ativos: usersResult.count || 0,
        posts_mural: postsResult.count || 0,
        reservas_salas: reservasResult.count || 0,
        solicitacoes_ti: tiResult.count || 0,
        trocas_proteina: proteinaResult.count || 0,
        agendamentos_portaria: portariaResult.count || 0,
      });
    } catch (error) {
      console.error('Error loading system stats:', error);
      toast.error('Erro ao carregar estatísticas do sistema');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('usuarios')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading users:', error);
        toast.error('Erro ao carregar usuários');
        setUsers([]);
        return;
      }

      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erro ao carregar usuários');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newUser.nome.trim() || !newUser.email.trim() || !newUser.senha.trim()) {
      toast.error('Preencha todos os campos obrigatórios!');
      return;
    }

    if (newUser.senha.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres!');
      return;
    }

    try {
      setLoading(true);

      const result = await SupabaseAuth.signUp(newUser.email, newUser.senha, {
        nome: newUser.nome,
        setor: newUser.setor,
        role: newUser.role,
      });

      if (result.needsEmailConfirmation) {
        toast.error('Confirmação de e-mail não está habilitada. Verifique a configuração do Supabase.');
        return;
      }

      toast.success('Usuário criado com sucesso!');
      setNewUser({ nome: '', email: '', senha: '', setor: 'Geral', role: 'colaborador' });
      setShowNewUserModal(false);
      await loadUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.message || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId: string, updates: Partial<User>) => {
    try {
      setLoading(true);

      const { error } = await supabase
        .from('usuarios')
        .update(updates)
        .eq('id', userId);

      if (error) {
        console.error('Error updating user:', error);
        toast.error('Erro ao atualizar usuário');
        return;
      }

      toast.success('Usuário atualizado com sucesso!');
      setEditingUser(null);
      await loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Erro ao atualizar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    const confirmation = confirm(`Tem certeza que deseja REMOVER PERMANENTEMENTE o usuário "${userName}"?\n\nEsta ação:\n- Excluirá o usuário da tabela de usuários\n- Removerá o acesso ao sistema\n- NÃO PODE ser desfeita\n\nSe você deseja apenas desativar o usuário temporariamente, use o botão de desativar.`);

    if (!confirmation) return;

    try {
      setLoading(true);

      const userToDelete = users.find(u => u.id === userId);
      if (!userToDelete) {
        toast.error('Usuário não encontrado');
        return;
      }

      // Remove related records from child tables to avoid FK constraint violations
      await supabase.from('sorteio_vencedores').delete().eq('usuario_id', userId);
      await supabase.from('sorteio_inscricoes').delete().eq('usuario_id', userId);
      await supabase.from('mural_likes').delete().eq('usuario_id', userId);
      await supabase.from('mural_comments').delete().eq('usuario_id', userId);
      await supabase.from('mural_posts').delete().eq('usuario_id', userId);
      await supabase.from('pontos').delete().eq('usuario_id', userId);
      await supabase.from('trocas_proteina').delete().eq('usuario_id', userId);
      await supabase.from('ti_solicitacoes').delete().eq('usuario_id', userId);
      await supabase.from('reservas_salas').delete().eq('usuario_id', userId);
      await supabase.from('rh_solicitacoes').delete().eq('usuario_id', userId);
      await supabase.from('ti_mensagens').delete().eq('usuario_id', userId);
      await supabase.from('aniversariantes_comentarios').delete().eq('usuario_id', userId);
      await supabase.from('reserva_veiculos').delete().eq('usuario_id', userId);

      const { error: deleteError } = await supabase
        .from('usuarios')
        .delete()
        .eq('id', userId);

      if (deleteError) {
        console.error('Error deleting user:', deleteError);
        toast.error(`Erro ao remover usuário: ${deleteError.message}`);
        return;
      }

      toast.success(`Usuário "${userName}" removido com sucesso!`);
      await loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erro ao remover usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (userId: string) => {
    const newPassword = prompt('Digite a nova senha (mínimo 6 caracteres):');
    if (!newPassword || newPassword.length < 6) {
      toast.error('Senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      setLoading(true);

      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        toast.error('Sessão expirada. Faça login novamente.');
        return;
      }

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-reset-password`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          newPassword,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error('Error resetting password:', result);
        toast.error(result.error || 'Erro ao alterar senha');
        return;
      }

      toast.success('Senha alterada com sucesso!');
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (formato: 'csv' | 'excel' | 'pdf') => {
    try {
      console.log(`[ADMIN] Exporting users as ${formato}...`);

      const { data, error } = await supabase
        .from('usuarios')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching users for export:', error);
        toast.error('Erro ao buscar usuários');
        return;
      }

      if (formato === 'csv') {
        const headers = ['ID', 'Nome', 'Email', 'Setor', 'Role', 'Ativo', 'Criado em'];
        const rows = data.map((u: any) => [
          u.id,
          u.nome,
          u.email,
          u.setor,
          u.role,
          u.ativo ? 'Sim' : 'Não',
          new Date(u.created_at).toLocaleDateString('pt-BR'),
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map((row: any[]) => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `usuarios-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Usuários exportados com sucesso!');
      } else {
        toast.success(`${data.length} usuários prontos para exportar como ${formato.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erro ao exportar dados');
    }
  };

  const handleExportActivities = async (formato: 'csv' | 'excel' | 'pdf') => {
    try {
      console.log(`[ADMIN] Exporting activities as ${formato}...`);

      const [reservas, solicitacoes, trocas, portaria] = await Promise.all([
        supabase.from('reservas_salas').select('*, usuarios(nome)').order('created_at', { ascending: false }),
        supabase.from('ti_solicitacoes').select('*').order('created_at', { ascending: false }),
        supabase.from('trocas_proteina').select('*').order('created_at', { ascending: false }),
        supabase.from('portaria_agendamentos').select('*, usuarios(nome)').order('created_at', { ascending: false }),
      ]);

      if (formato === 'csv') {
        const headers = ['Tipo', 'Data', 'Usuário', 'Detalhes', 'Status'];
        const rows: any[] = [];

        reservas.data?.forEach((r: any) => {
          rows.push([
            'Reserva de Sala',
            new Date(r.created_at).toLocaleDateString('pt-BR'),
            r.usuarios?.nome || 'N/A',
            `${r.sala} - ${r.motivo}`,
            r.status,
          ]);
        });

        solicitacoes.data?.forEach((s: any) => {
          rows.push([
            'Solicitação TI',
            new Date(s.created_at).toLocaleDateString('pt-BR'),
            s.solicitante_nome,
            `${s.equipamento} - ${s.descricao}`,
            s.status,
          ]);
        });

        trocas.data?.forEach((t: any) => {
          rows.push([
            'Troca Proteína',
            new Date(t.created_at).toLocaleDateString('pt-BR'),
            'N/A',
            `${t.proteina_original} → ${t.proteina_nova}`,
            t.status,
          ]);
        });

        portaria.data?.forEach((p: any) => {
          rows.push([
            'Agendamento Portaria',
            new Date(p.created_at).toLocaleDateString('pt-BR'),
            p.usuarios?.nome || 'N/A',
            `Visitante: ${p.visitante}`,
            p.status,
          ]);
        });

        const csvContent = [
          headers.join(','),
          ...rows.map((row: any[]) => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `atividades-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Relatório de atividades exportado!');
      } else {
        const total = (reservas.data?.length || 0) + (solicitacoes.data?.length || 0) +
                     (trocas.data?.length || 0) + (portaria.data?.length || 0);
        toast.success(`${total} atividades prontas para exportar como ${formato.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Activities export error:', error);
      toast.error('Erro ao exportar atividades');
    }
  };

  const handleExportTrocas = async (formato: 'csv' | 'excel') => {
    try {
      console.log(`[ADMIN] Exporting protein exchanges as ${formato}...`);

      const { data, error } = await supabase
        .from('trocas_proteina')
        .select('*')
        .order('data_troca', { ascending: false });

      if (error) {
        console.error('Error fetching protein exchanges:', error);
        toast.error('Erro ao buscar trocas de proteínas');
        return;
      }

      if (formato === 'csv') {
        const headers = ['Data', 'Proteína Original', 'Proteína Nova', 'Refeição', 'Observações', 'Status'];
        const rows = data.map((t: any) => [
          new Date(t.data_troca).toLocaleDateString('pt-BR'),
          t.proteina_original,
          t.proteina_nova,
          t.refeicao || 'N/A',
          t.observacoes || '',
          t.status,
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map((row: any[]) => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `trocas-proteina-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Relatório de trocas de proteínas exportado!');
      } else {
        toast.success(`${data.length} trocas prontas para exportar como ${formato.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Protein exchanges export error:', error);
      toast.error('Erro ao exportar trocas de proteínas');
    }
  };

  const handleExportEquipamentos = async (formato: 'csv' | 'excel') => {
    try {
      console.log(`[ADMIN] Exporting equipment requests as ${formato}...`);

      const { data, error } = await supabase
        .from('ti_solicitacoes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching equipment requests:', error);
        toast.error('Erro ao buscar solicitações de equipamentos');
        return;
      }

      if (formato === 'csv') {
        const headers = ['Data', 'Solicitante', 'Email', 'Equipamento', 'Descrição', 'Prioridade', 'Status'];
        const rows = data.map((e: any) => [
          new Date(e.created_at).toLocaleDateString('pt-BR'),
          e.solicitante_nome,
          e.solicitante_email || '',
          e.equipamento,
          e.descricao,
          e.prioridade,
          e.status,
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map((row: any[]) => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `equipamentos-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Relatório de equipamentos exportado!');
      } else {
        toast.success(`${data.length} solicitações prontas para exportar como ${formato.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Equipment requests export error:', error);
      toast.error('Erro ao exportar solicitações de equipamentos');
    }
  };

  const handleExportReservas = async (formato: 'csv' | 'excel') => {
    try {
      console.log(`[ADMIN] Exporting room reservations as ${formato}...`);

      const { data, error } = await supabase
        .from('reservas_salas')
        .select('*, usuarios(nome)')
        .order('data_reserva', { ascending: false });

      if (error) {
        console.error('Error fetching room reservations:', error);
        toast.error('Erro ao buscar reservas de salas');
        return;
      }

      if (formato === 'csv') {
        const headers = ['Data', 'Sala', 'Usuário', 'Motivo', 'Horário Início', 'Horário Fim', 'Observações', 'Status'];
        const rows = data.map((r: any) => [
          new Date(r.data_reserva).toLocaleDateString('pt-BR'),
          r.sala,
          r.usuarios?.nome || 'N/A',
          r.motivo,
          r.hora_inicio,
          r.hora_fim,
          r.observacoes || '',
          r.status,
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map((row: any[]) => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `reservas-salas-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success('Relatório de reservas exportado!');
      } else {
        toast.success(`${data.length} reservas prontas para exportar como ${formato.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Room reservations export error:', error);
      toast.error('Erro ao exportar reservas de salas');
    }
  };

  const handleExportBackup = async (formato: 'json' | 'sql') => {
    try {
      console.log(`[ADMIN] Creating backup as ${formato}...`);
      toast.info('Gerando backup... Isso pode levar alguns segundos.');

      const [usuarios, muralPosts, reservasSalas, tiSolicitacoes, trocasProteina, portariaAgendamentos, reservaVeiculos] = await Promise.all([
        supabase.from('usuarios').select('*'),
        supabase.from('mural_posts').select('*'),
        supabase.from('reservas_salas').select('*'),
        supabase.from('ti_solicitacoes').select('*'),
        supabase.from('trocas_proteina').select('*'),
        supabase.from('portaria_agendamentos').select('*'),
        supabase.from('reserva_veiculos').select('*'),
      ]);

      const backupData = {
        timestamp: new Date().toISOString(),
        version: '1.0',
        data: {
          usuarios: usuarios.data || [],
          mural_posts: muralPosts.data || [],
          reservas_salas: reservasSalas.data || [],
          ti_solicitacoes: tiSolicitacoes.data || [],
          trocas_proteina: trocasProteina.data || [],
          portaria_agendamentos: portariaAgendamentos.data || [],
          reserva_veiculos: reservaVeiculos.data || [],
        },
      };

      const totalRecords = Object.values(backupData.data).reduce((sum: number, arr: any) => sum + arr.length, 0);

      if (formato === 'json') {
        const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `backup-completo-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
        toast.success(`Backup JSON baixado com sucesso! (${totalRecords} registros)`);
      } else {
        toast.success(`Backup SQL: ${totalRecords} registros prontos. Use ferramentas do Supabase para backup SQL completo.`);
      }
    } catch (error) {
      console.error('Backup export error:', error);
      toast.error('Erro ao gerar backup');
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const handleCardapioUpload = async () => {
    if (!cardapioFile) {
      toast.error('Selecione um arquivo Excel ou JSON');
      return;
    }

    try {
      setUploadingCardapio(true);
      const fileName = cardapioFile.name.toLowerCase();
      const isJSON = fileName.endsWith('.json');

      if (isJSON) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const content = e.target?.result as string;
            const jsonData = JSON.parse(content);

            if (!Array.isArray(jsonData)) {
              toast.error('Formato JSON inválido. Deve ser um array de objetos.');
              return;
            }

            const cardapioItens: any[] = [];

            jsonData.forEach((item: any) => {
              const data = item.data;
              const diaSemana = item.dia_semana;
              const proteinas = item.proteina || [];
              const acompanhamentos = item.acompanhamentos || [];
              const saladas = item.saladas || [];
              const sobremesa = item.sobremesa || '';

              if (data && diaSemana) {
                const partes = data.split('/');
                if (partes.length === 3) {
                  const dataFormatada = `${partes[2]}-${partes[1].padStart(2, '0')}-${partes[0].padStart(2, '0')}`;

                  cardapioItens.push({
                    data: dataFormatada,
                    tipo: 'padrao',
                    dia_semana: diaSemana,
                    proteinas: Array.isArray(proteinas) ? proteinas : [],
                    acompanhamentos: Array.isArray(acompanhamentos) ? acompanhamentos : [],
                    saladas: Array.isArray(saladas) ? saladas : [],
                    sobremesa: sobremesa,
                    publicado: true
                  });
                }
              }
            });

            if (cardapioItens.length === 0) {
              toast.error('Nenhum item válido encontrado no JSON');
              return;
            }

            const primeiraData = cardapioItens[0].data;
            const mes = primeiraData.substring(0, 7);

            const { error: deleteError } = await supabase
              .from('cardapio_itens')
              .delete()
              .like('data', `${mes}%`);

            if (deleteError) {
              console.error('[ADMIN] Erro ao limpar cardápio anterior:', deleteError);
            }

            const { error: insertError } = await supabase
              .from('cardapio_itens')
              .insert(cardapioItens);

            if (insertError) throw insertError;

            toast.success(`${cardapioItens.length} itens do cardápio importados com sucesso! O cardápio já está disponível na página de Cardápio.`, {
              duration: 5000,
            });
            setCardapioFile(null);

          } catch (error: any) {
            console.error('[ADMIN] Erro ao processar JSON:', error);
            toast.error('Erro ao processar arquivo: ' + error.message);
          } finally {
            setUploadingCardapio(false);
          }
        };

        reader.readAsText(cardapioFile);

      } else {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const data = e.target?.result;
            const workbook = XLSX.read(data, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            const cardapioItens: any[] = [];

            jsonData.forEach((row: any) => {
              const data = row['Data'] || row['DATA'] || row['data'];
              const diaSemana = row['Dia'] || row['DIA'] || row['dia'] || row['Dia da Semana'] || row['dia_semana'];
              const proteinas = row['Proteína'] || row['Proteinas'] || row['PROTEINA'] || row['proteina'] || '';
              const acompanhamentos = row['Acompanhamentos'] || row['ACOMPANHAMENTOS'] || row['acompanhamentos'] || '';
              const saladas = row['Saladas'] || row['SALADAS'] || row['saladas'] || '';
              const sobremesa = row['Sobremesa'] || row['SOBREMESA'] || row['sobremesa'] || '';

              if (data && diaSemana) {
                let dataFormatada: string;
                if (typeof data === 'number') {
                  const excelDate = XLSX.SSF.parse_date_code(data);
                  dataFormatada = `${excelDate.y}-${String(excelDate.m).padStart(2, '0')}-${String(excelDate.d).padStart(2, '0')}`;
                } else {
                  const partes = data.toString().split('/');
                  if (partes.length === 3) {
                    dataFormatada = `${partes[2]}-${partes[1].padStart(2, '0')}-${partes[0].padStart(2, '0')}`;
                  } else {
                    return;
                  }
                }

                cardapioItens.push({
                  data: dataFormatada,
                  tipo: 'padrao',
                  dia_semana: diaSemana,
                  proteinas: proteinas.toString().split(',').map((p: string) => p.trim()).filter((p: string) => p),
                  acompanhamentos: acompanhamentos.toString().split(',').map((a: string) => a.trim()).filter((a: string) => a),
                  saladas: saladas.toString().split(',').map((s: string) => s.trim()).filter((s: string) => s),
                  sobremesa: sobremesa.toString().trim(),
                  publicado: true
                });
              }
            });

            if (cardapioItens.length === 0) {
              toast.error('Nenhum item válido encontrado no Excel');
              return;
            }

            const primeiraData = cardapioItens[0].data;
            const mes = primeiraData.substring(0, 7);

            const { error: deleteError } = await supabase
              .from('cardapio_itens')
              .delete()
              .like('data', `${mes}%`);

            if (deleteError) {
              console.error('[ADMIN] Erro ao limpar cardápio anterior:', deleteError);
            }

            const { error: insertError } = await supabase
              .from('cardapio_itens')
              .insert(cardapioItens);

            if (insertError) throw insertError;

            toast.success(`${cardapioItens.length} itens do cardápio importados com sucesso! O cardápio já está disponível na página de Cardápio.`, {
              duration: 5000,
            });
            setCardapioFile(null);

          } catch (error: any) {
            console.error('[ADMIN] Erro ao processar Excel:', error);
            toast.error('Erro ao processar arquivo: ' + error.message);
          } finally {
            setUploadingCardapio(false);
          }
        };

        reader.readAsBinaryString(cardapioFile);
      }

    } catch (error: any) {
      console.error('[ADMIN] Erro no upload:', error);
      toast.error('Erro ao fazer upload: ' + error.message);
      setUploadingCardapio(false);
    }
  };

  const loadBrindes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('sorteio_brindes')
        .select(`
          *,
          vencedor:vencedor_id(nome)
        `)
        .order('data_sorteio', { ascending: false });

      if (error) throw error;

      if (data) {
        const brindesComInscricoes = await Promise.all(
          data.map(async (brinde) => {
            const { count } = await supabase
              .from('sorteio_inscricoes')
              .select('*', { count: 'exact', head: true })
              .eq('brinde_id', brinde.id);

            const { data: vencedores, error: vencedoresError } = await supabase
              .from('sorteio_vencedores')
              .select('usuario_id, usuario:usuario_id(nome)')
              .eq('brinde_id', brinde.id)
              .order('criado_em', { ascending: true });

            if (vencedoresError) {
              console.error('Erro ao buscar vencedores:', vencedoresError);
            }

            return {
              ...brinde,
              total_inscricoes: count || 0,
              vencedores: vencedores || []
            } as Brinde;
          })
        );

        setBrindes(brindesComInscricoes);
      }
    } catch (error: any) {
      console.error('Erro ao carregar brindes:', error);
      toast.error('Erro ao carregar brindes');
    } finally {
      setLoading(false);
    }
  };

  const handleCriarBrinde = async () => {
    if (!novoBrinde.titulo || !novoBrinde.descricao || !novoBrinde.data_sorteio) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (!novoBrinde.quantidade || novoBrinde.quantidade < 1) {
      toast.error('Informe uma quantidade válida (mínimo 1)');
      return;
    }

    try {
      const { error } = await supabase
        .from('sorteio_brindes')
        .insert({
          titulo: novoBrinde.titulo,
          descricao: novoBrinde.descricao,
          imagem_url: novoBrinde.imagem_url || null,
          data_sorteio: novoBrinde.data_sorteio,
          quantidade: novoBrinde.quantidade, // ✅ grava quantidade no banco
          status: 'ativo',
          criado_por: user?.id
        });

      if (error) throw error;

      toast.success('Brinde criado com sucesso!');
      setShowNovoBrindeModal(false);
      setNovoBrinde({ titulo: '', descricao: '', imagem_url: '', data_sorteio: '', quantidade: 1 });
      loadBrindes();
    } catch (error: any) {
      console.error('Erro ao criar brinde:', error);
      toast.error('Erro ao criar brinde');
    }
  };

  const handleRealizarSorteio = async (brindeId: string) => {
    try {
      const { data: brinde, error: brindeError } = await supabase
        .from('sorteio_brindes')
        .select('quantidade, titulo')
        .eq('id', brindeId)
        .single();

      if (brindeError) throw brindeError;
      if (!brinde) throw new Error('Brinde não encontrado');

      const { data: inscricoes, error: inscricoesError } = await supabase
        .from('sorteio_inscricoes')
        .select('usuario_id')
        .eq('brinde_id', brindeId);

      if (inscricoesError) throw inscricoesError;

      if (!inscricoes || inscricoes.length === 0) {
        toast.error('Não há inscritos para este sorteio');
        return;
      }

      const quantidadeSortear = Math.min(brinde.quantidade, inscricoes.length);

      const mensagemConfirmacao = inscricoes.length < brinde.quantidade
        ? `Há apenas ${inscricoes.length} inscritos para ${brinde.quantidade} brindes.\nSerão sorteados ${quantidadeSortear} vencedores.\n\nDeseja continuar?`
        : `Sortear ${quantidadeSortear} vencedor(es) para "${brinde.titulo}"?\n\nTotal de inscritos: ${inscricoes.length}`;

      if (!confirm(mensagemConfirmacao)) {
        return;
      }

      setSorteandoBrindeId(brindeId);

      const inscricoesDisponiveis = [...inscricoes];
      const vencedores: string[] = [];

      for (let i = 0; i < quantidadeSortear; i++) {
        const vencedorIndex = Math.floor(Math.random() * inscricoesDisponiveis.length);
        const vencedorId = inscricoesDisponiveis[vencedorIndex].usuario_id;
        vencedores.push(vencedorId);
        inscricoesDisponiveis.splice(vencedorIndex, 1);
      }

      const agora = new Date().toISOString();

      const { error: vencedoresError } = await supabase
        .from('sorteio_vencedores')
        .insert(
          vencedores.map(vencedorId => ({
            brinde_id: brindeId,
            usuario_id: vencedorId
          }))
        );

      if (vencedoresError) throw vencedoresError;

      const { error: updateError } = await supabase
        .from('sorteio_brindes')
        .update({
          status: 'sorteado',
          vencedor_id: vencedores[0],
          sorteado_em: agora
        })
        .eq('id', brindeId);

      if (updateError) throw updateError;

      const { data: vencedoresNomes } = await supabase
        .from('usuarios')
        .select('nome')
        .in('id', vencedores);

      const nomesVencedores = vencedoresNomes?.map(v => v.nome).join(', ') || '';

      const mensagemSucesso = quantidadeSortear === 1
        ? `Sorteio realizado! Vencedor: ${nomesVencedores}`
        : `Sorteio realizado! ${vencedores.length} vencedores: ${nomesVencedores}`;

      toast.success(mensagemSucesso, { duration: 6000 });
      loadBrindes();
    } catch (error: any) {
      console.error('Erro ao realizar sorteio:', error);
      toast.error('Erro ao realizar sorteio: ' + (error.message || 'Tente novamente'));
    } finally {
      setSorteandoBrindeId(null);
    }
  };

  const handleCancelarBrinde = async (brindeId: string) => {
    if (!confirm('Tem certeza que deseja cancelar este brinde?')) return;

    try {
      const { error } = await supabase
        .from('sorteio_brindes')
        .update({ status: 'cancelado' })
        .eq('id', brindeId);

      if (error) throw error;

      toast.success('Brinde cancelado');
      loadBrindes();
    } catch (error: any) {
      console.error('Erro ao cancelar brinde:', error);
      toast.error('Erro ao cancelar brinde');
    }
  };

  const handleDeletarBrinde = async (brindeId: string) => {
    if (!confirm('Tem certeza que deseja deletar este brinde? Esta ação não pode ser desfeita.')) return;

    try {
      const { error } = await supabase
        .from('sorteio_brindes')
        .delete()
        .eq('id', brindeId);

      if (error) throw error;

      toast.success('Brinde deletado');
      loadBrindes();
    } catch (error: any) {
      console.error('Erro ao deletar brinde:', error);
      toast.error('Erro ao deletar brinde');
    }
  };

  const allTabs = [
    { key: 'dashboard' as const, label: 'Dashboard', icon: BarChart3, visible: isAdmin || isRH },
    { key: 'users' as const, label: 'Usuários', icon: Users, visible: canManageUsers },
    { key: 'contacts' as const, label: 'Contatos', icon: Phone, visible: canManageContacts },
    { key: 'cardapio' as const, label: 'Cardápio', icon: ChefHat, visible: isAdmin || isRH },
    { key: 'brindes' as const, label: 'Sorteio Brindes', icon: Gift, visible: canManageSorteios },
    { key: 'reports' as const, label: 'Relatórios', icon: Download, visible: canAccessReports },
  ];
  const tabs = allTabs.filter(t => t.visible);

  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.key === activeTab)) {
      setActiveTab(tabs[0].key);
    }
  }, [tabs, activeTab]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-headline-lg text-on-surface">Painel Administrativo</h1>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <a
                href="#/admin/google-calendar"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 font-bold text-sm"
              >
                <Calendar className="w-4 h-4" /> Google Calendar
              </a>
            )}
            <div className="text-sm text-secondary">
              Logado como: {user?.name} ({user?.role})
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-surface-container p-1 rounded-lg w-fit">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                activeTab === tab.key
                  ? 'bg-white dark:bg-gray-800 text-primary'
                  : 'text-secondary hover:text-on-surface'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stats && Object.entries(stats).map(([key, value]) => (
                <div key={key} className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-secondary capitalize">
                        {key.replace(/_/g, ' ')}
                      </p>
                      <p className="text-3xl font-bold text-on-surface mt-2">{value}</p>
                    </div>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-on-surface">Gerenciar Usuários</h2>
              <button
                onClick={() => setShowNewUserModal(true)}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
              >
                <UserPlus className="w-4 h-4" />
                <span>Novo Usuário</span>
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-outline-variant dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-outline-variant">
                  <thead className="bg-surface-container-low">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                        Usuário
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                        Setor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary uppercase tracking-wider">
                        Criado em
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-secondary uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-outline-variant dark:divide-gray-700">
                    {users.map((userData) => (
                      <tr key={userData.id} className="hover:bg-surface-container-low">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-on-surface">{userData.nome}</div>
                            <div className="text-sm text-secondary">{userData.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary/10 text-primary">
                            {userData.setor}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            userData.role === 'admin' ? 'bg-red-100 text-red-800' :
                            userData.role === 'moderador' ? 'bg-orange-100 text-orange-800' :
                            userData.role === 'portaria' ? 'bg-blue-100 text-blue-800' :
                            userData.role === 'ti' ? 'bg-cyan-100 text-cyan-800' :
                            userData.role === 'frota' ? 'bg-teal-100 text-teal-800' :
                            userData.role === 'sorteio' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {userData.role === 'ti' ? 'TI' : userData.role === 'frota' ? 'Frota' : userData.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            userData.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userData.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary">
                          {formatDate(userData.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => setEditingUser(userData)}
                              className="text-primary hover:text-primary-dark"
                              title="Editar usuário"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleResetPassword(userData.id)}
                              className="text-yellow-600 hover:text-yellow-700"
                              title="Resetar senha"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleUpdateUser(userData.id, { ativo: !userData.ativo })}
                              className={userData.ativo ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                              title={userData.ativo ? 'Desativar usuário' : 'Ativar usuário'}
                            >
                              {userData.ativo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleDeleteUser(userData.id, userData.nome)}
                              className="text-error hover:text-error"
                              title="Remover usuário permanentemente"
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <ContactsManager />
        )}

        {/* Cardapio Tab */}
        {activeTab === 'cardapio' && (
          <div className="space-y-6">
            <h2 className="text-lg font-bold text-on-surface">Gerenciar Cardápio</h2>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 border border-outline-variant dark:border-gray-700">
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <ChefHat className="w-16 h-16 mx-auto text-primary mb-4" />
                  <h3 className="font-display text-headline-lg text-on-surface mb-2">Upload de Cardápio Mensal</h3>
                  <p className="text-secondary">
                    Faça upload do arquivo Excel com o cardápio do mês. O sistema irá processar automaticamente e substituir os dados do mês correspondente.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-primary/5 border border-primary/30 rounded-lg p-4">
                      <h4 className="font-semibold text-on-surface mb-2">📊 Formato Excel:</h4>
                      <ul className="text-sm text-secondary space-y-1 ml-4">
                        <li>• <strong>Data</strong>: DD/MM/AAAA</li>
                        <li>• <strong>Dia</strong>: Dia da semana</li>
                        <li>• <strong>Proteína</strong>: Separado por vírgula</li>
                        <li>• <strong>Acompanhamentos</strong>: Separado por vírgula</li>
                        <li>• <strong>Saladas</strong>: Separado por vírgula</li>
                        <li>• <strong>Sobremesa</strong>: Texto simples</li>
                      </ul>
                    </div>

                    <div className="bg-primary/5 border border-primary/30 rounded-lg p-4">
                      <h4 className="font-semibold text-on-surface mb-2">📄 Formato JSON:</h4>
                      <ul className="text-sm text-secondary space-y-1 ml-4">
                        <li>• <strong>data</strong>: "DD/MM/AAAA"</li>
                        <li>• <strong>dia_semana</strong>: "Segunda-feira"</li>
                        <li>• <strong>proteina</strong>: ["Item 1", "Item 2"]</li>
                        <li>• <strong>acompanhamentos</strong>: ["Item 1"]</li>
                        <li>• <strong>saladas</strong>: ["Item 1"]</li>
                        <li>• <strong>sobremesa</strong>: "Texto"</li>
                      </ul>
                    </div>
                  </div>

                  <div className="border-2 border-dashed border-outline-variant rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                    <input
                      type="file"
                      accept=".xlsx,.xls,.json"
                      onChange={(e) => setCardapioFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="cardapio-upload"
                      disabled={uploadingCardapio}
                    />
                    <label
                      htmlFor="cardapio-upload"
                      className="cursor-pointer"
                    >
                      <Upload className="w-12 h-12 mx-auto text-secondary mb-3" />
                      <p className="text-sm font-medium text-on-surface mb-1">
                        {cardapioFile ? cardapioFile.name : 'Clique para selecionar arquivo Excel ou JSON'}
                      </p>
                      <p className="text-xs text-secondary">
                        Arquivos .xlsx, .xls ou .json
                      </p>
                    </label>
                  </div>

                  {cardapioFile && (
                    <div className="flex gap-3">
                      <button
                        onClick={handleCardapioUpload}
                        disabled={uploadingCardapio}
                        className="flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition-colors disabled:bg-secondary disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                      >
                        {uploadingCardapio ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Processando...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-5 h-5" />
                            <span>Importar Cardápio</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setCardapioFile(null)}
                        disabled={uploadingCardapio}
                        className="px-6 py-3 border border-outline-variant rounded-lg hover:bg-surface-container-low transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-8 pt-8 border-t border-outline-variant">
                  <h4 className="font-semibold text-on-surface mb-3">ℹ️ Como funciona:</h4>
                  <ol className="text-sm text-secondary space-y-2 ml-4">
                    <li>1. Prepare o arquivo Excel ou JSON com as colunas corretas</li>
                    <li>2. Selecione o arquivo clicando na área acima</li>
                    <li>3. Clique em "Importar Cardápio"</li>
                    <li>4. O sistema irá:
                      <ul className="ml-6 mt-1 space-y-1">
                        <li>• Deletar o cardápio do mês correspondente no banco de dados</li>
                        <li>• Importar todos os novos itens</li>
                        <li>• Atualizar automaticamente na página de <strong>Cardápio</strong></li>
                        <li>• Exibir no Dashboard e em todas as telas da intranet</li>
                      </ul>
                    </li>
                  </ol>

                  <div className="mt-6 bg-primary/5 border border-primary/30 rounded-lg p-4">
                    <p className="text-sm text-secondary">
                      <strong>📌 Importante:</strong> Após o upload bem-sucedido, o cardápio estará <strong>imediatamente disponível</strong> na página de Cardápio para todos os usuários. Não é necessário nenhuma ação adicional.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Brindes Tab */}
        {activeTab === 'brindes' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-on-surface">Gerenciar Sorteio de Brindes</h2>
              <button
                onClick={() => setShowNovoBrindeModal(true)}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
              >
                <Gift className="w-5 h-5" />
                <span>Novo Brinde</span>
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : brindes.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <Gift className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-700 mb-2">Nenhum brinde cadastrado</h3>
                <p className="text-gray-600 mb-4">Crie o primeiro brinde para começar</p>
                <button
                  onClick={() => setShowNovoBrindeModal(true)}
                  className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Criar Primeiro Brinde
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {brindes.map((brinde) => (
                  <div key={brinde.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
                    {brinde.imagem_url ? (
                      <img src={brinde.imagem_url} alt={brinde.titulo} className="w-full h-48 object-cover" />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center">
                        <Gift className="w-20 h-20 text-white opacity-50" />
                      </div>
                    )}

                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-bold text-on-surface">{brinde.titulo}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          brinde.status === 'ativo' ? 'bg-green-100 text-green-800' :
                          brinde.status === 'sorteado' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {brinde.status}
                        </span>
                      </div>

                      <p className="text-secondary text-sm mb-2 line-clamp-2">{brinde.descricao}</p>

                      <p className="text-sm font-medium text-on-surface mb-2">
                        🎁 Quantidade: {brinde.quantidade ?? 1}
                      </p>

                      <div className="space-y-2 mb-4 text-sm">
                        <div className="flex items-center text-secondary">
                          <Calendar className="w-4 h-4 mr-2" />
                          {format(new Date(brinde.data_sorteio), 'dd/MM/yyyy')}
                        </div>
                        <div className="flex items-center text-gray-600">
                          <Users className="w-4 h-4 mr-2" />
                          {brinde.total_inscricoes} inscritos
                        </div>
                      </div>

                      {brinde.status === 'sorteado' && brinde.vencedores && brinde.vencedores.length > 0 && (
                        <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 mb-4">
                          <div className="flex items-start">
                            <Trophy className="w-5 h-5 text-primary mr-2 mt-1 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs text-on-surface font-medium mb-2">
                                {brinde.vencedores.length > 1 ? `${brinde.vencedores.length} Vencedores` : 'Vencedor'}
                              </p>
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {brinde.vencedores.map((vencedor, index) => (
                                  <p key={index} className="text-sm text-on-surface">
                                    {index + 1}. {vencedor.usuario?.nome || 'Desconhecido'}
                                  </p>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="space-y-2">
                        {brinde.status === 'ativo' && (
                          <button
                            onClick={() => handleRealizarSorteio(brinde.id)}
                            disabled={sorteandoBrindeId === brinde.id || (brinde.total_inscricoes || 0) === 0}
                            className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary-dark transition-colors disabled:bg-secondary disabled:cursor-not-allowed flex items-center justify-center"
                          >
                            {sorteandoBrindeId === brinde.id ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Sorteando...
                              </>
                            ) : (
                              <>
                                <Trophy className="w-4 h-4 mr-2" />
                                Realizar Sorteio
                              </>
                            )}
                          </button>
                        )}

                        <div className="flex gap-2">
                          {brinde.status === 'ativo' && (
                            <button
                              onClick={() => handleCancelarBrinde(brinde.id)}
                              className="flex-1 bg-secondary text-white py-2 rounded-lg hover:bg-secondary transition-colors text-sm"
                            >
                              Cancelar
                            </button>
                          )}
                          <button
                            onClick={() => handleDeletarBrinde(brinde.id)}
                            className="flex-1 bg-error text-white py-2 rounded-lg hover:bg-error transition-colors text-sm"
                          >
                            Deletar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modal Novo Brinde */}
        {showNovoBrindeModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-display text-headline-lg text-on-surface">Criar Novo Brinde</h3>
                  <button
                    onClick={() => {
                      setShowNovoBrindeModal(false);
                      setNovoBrinde({ titulo: '', descricao: '', imagem_url: '', data_sorteio: '', quantidade: 1 });
                    }}
                    className="text-secondary hover:text-on-surface"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Título *
                    </label>
                    <input
                      type="text"
                      value={novoBrinde.titulo}
                      onChange={(e) => setNovoBrinde({ ...novoBrinde, titulo: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      placeholder="Ex: Cesta de Natal"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Descrição *
                    </label>
                    <textarea
                      value={novoBrinde.descricao}
                      onChange={(e) => setNovoBrinde({ ...novoBrinde, descricao: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      rows={4}
                      placeholder="Descreva o brinde em detalhes..."
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-on-surface mb-2">
                        URL da Imagem (opcional)
                      </label>
                      <input
                        type="url"
                        value={novoBrinde.imagem_url}
                        onChange={(e) => setNovoBrinde({ ...novoBrinde, imagem_url: e.target.value })}
                        className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        placeholder="https://exemplo.com/imagem.jpg"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-on-surface mb-2">
                        Data do Sorteio *
                      </label>
                      <input
                        type="date"
                        value={novoBrinde.data_sorteio}
                        onChange={(e) => setNovoBrinde({ ...novoBrinde, data_sorteio: e.target.value })}
                        className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">
                      Quantidade de brindes *
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={novoBrinde.quantidade}
                      onChange={(e) =>
                        setNovoBrinde({
                          ...novoBrinde,
                          quantidade: Number(e.target.value) || 1,
                        })
                      }
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      placeholder="Ex: 10"
                    />
                    <p className="mt-1 text-xs text-secondary">
                      Número de vencedores que serão sorteados. Ex: 10 = sorteia 10 pessoas diferentes.
                    </p>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => {
                        setShowNovoBrindeModal(false);
                        setNovoBrinde({ titulo: '', descricao: '', imagem_url: '', data_sorteio: '', quantidade: 1 });
                      }}
                      className="flex-1 px-4 py-2 border border-outline-variant text-on-surface rounded-lg hover:bg-surface-container-low transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleCriarBrinde}
                      className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      Criar Brinde
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <h2 className="text-lg font-bold text-on-surface">Relatórios e Exportações</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                <h3 className="text-lg font-bold text-on-surface mb-4">Ranking de Usuários</h3>
                <p className="text-sm text-secondary mb-4">
                  Exportar ranking completo com pontuação de gamificação
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors text-sm"
                  >
                    Exportar CSV
                  </button>
                  <button
                    onClick={() => handleExport('excel')}
                    className="w-full bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors text-sm"
                  >
                    Exportar Excel
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="w-full bg-error text-white px-4 py-2 rounded-lg hover:bg-error transition-colors text-sm"
                  >
                    Exportar PDF
                  </button>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                <h3 className="text-lg font-bold text-on-surface mb-4">Relatório de Atividades</h3>
                <p className="text-sm text-secondary mb-4">
                  Relatório detalhado de todas as atividades do sistema
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => handleExportActivities('csv')}
                    className="w-full bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors text-sm"
                  >
                    Exportar Atividades CSV
                  </button>
                  <button
                    onClick={() => handleExportActivities('excel')}
                    className="w-full bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors text-sm"
                  >
                    Exportar Atividades Excel
                  </button>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                <h3 className="text-lg font-bold text-on-surface mb-4">Trocas de Proteínas</h3>
                <p className="text-sm text-secondary mb-4">
                  Relatório completo de todas as trocas de proteínas
                </p>
                <button
                  onClick={exportTrocasProteinasToExcel}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white px-4 py-3 rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  Exportar Excel
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                <h3 className="text-lg font-bold text-on-surface mb-4">Solicitações de Equipamentos</h3>
                <p className="text-sm text-secondary mb-4">
                  Relatório de todas as solicitações de equipamentos TI
                </p>
                <button
                  onClick={exportEquipamentosToExcel}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white px-4 py-3 rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  Exportar Excel
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                <h3 className="text-lg font-bold text-on-surface mb-4">Reservas de Salas</h3>
                <p className="text-sm text-secondary mb-4">
                  Relatório completo de reservas de salas
                </p>
                <button
                  onClick={exportReservasSalasToExcel}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white px-4 py-3 rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  Exportar Excel
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                <h3 className="text-lg font-bold text-on-surface mb-4">Agendamentos Portaria</h3>
                <p className="text-sm text-secondary mb-4">
                  Relatório de agendamentos da portaria
                </p>
                <button
                  onClick={exportAgendamentosPortariaToExcel}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white px-4 py-3 rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  Exportar Excel
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                <h3 className="text-lg font-bold text-on-surface mb-4">Reservas de Veículos</h3>
                <p className="text-sm text-secondary mb-4">
                  Relatório de reservas de veículos
                </p>
                <button
                  onClick={exportReservasVeiculosToExcel}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white px-4 py-3 rounded-lg hover:bg-primary-dark transition-colors"
                >
                  <FileSpreadsheet className="w-5 h-5" />
                  Exportar Excel
                </button>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant md:col-span-2">
                <h3 className="text-lg font-bold text-on-surface mb-4">Backup do Sistema</h3>
                <p className="text-sm text-secondary mb-4">
                  Fazer backup completo dos dados do sistema
                </p>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleExportBackup('json')}
                    className="flex-1 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary transition-colors text-sm"
                  >
                    Backup JSON
                  </button>
                  <button
                    onClick={() => handleExportBackup('sql')}
                    className="flex-1 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary transition-colors text-sm"
                  >
                    Backup SQL
                  </button>
                </div>
              </div>
            </div>

            {stats && (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-outline-variant dark:border-gray-700">
                <h3 className="text-lg font-bold text-on-surface mb-4">Estatísticas Resumidas</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{stats.usuarios_ativos}</div>
                    <div className="text-sm text-secondary">Usuários Ativos</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{stats.posts_mural}</div>
                    <div className="text-sm text-secondary">Posts no Mural</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{stats.reservas_salas}</div>
                    <div className="text-sm text-secondary">Reservas</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{stats.solicitacoes_ti}</div>
                    <div className="text-sm text-secondary">Solicitações TI</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-error">{stats.trocas_proteina}</div>
                    <div className="text-sm text-secondary">Trocas Proteína</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{stats.agendamentos_portaria}</div>
                    <div className="text-sm text-secondary">Agendamentos</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* New User Modal */}
        {showNewUserModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60]">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md mx-4">
              <div className="p-6">
                <h2 className="text-lg font-bold text-on-surface mb-6">Novo Usuário</h2>
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">Nome</label>
                    <input
                      type="text"
                      value={newUser.nome}
                      onChange={(e) => setNewUser({ ...newUser, nome: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">Email</label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">Senha</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newUser.senha}
                        onChange={(e) => setNewUser({ ...newUser, senha: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        minLength={6}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">Setor</label>
                    <select
                      value={newUser.setor}
                      onChange={(e) => setNewUser({ ...newUser, setor: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="Geral">Geral</option>
                      <option value="TI">TI</option>
                      <option value="RH">RH</option>
                      <option value="Comercial">Comercial</option>
                      <option value="Financeiro">Financeiro</option>
                      <option value="Operações">Operações</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">Perfil</label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="colaborador">Colaborador</option>
                      <option value="moderador">Moderador</option>
                      <option value="portaria">Portaria</option>
                      <option value="ti">TI</option>
                      <option value="frota">Frota</option>
                      <option value="sorteio">Sorteio</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowNewUserModal(false)}
                      className="flex-1 px-4 py-2 border border-outline-variant text-on-surface rounded-lg hover:bg-surface-container-low transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      Criar Usuário
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60]">
            <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md mx-4">
              <div className="p-6">
                <h2 className="text-lg font-bold text-on-surface mb-6">Editar Usuário</h2>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleUpdateUser(editingUser.id, {
                    nome: editingUser.nome,
                    email: editingUser.email,
                    setor: editingUser.setor,
                    role: editingUser.role
                  });
                }} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">Nome</label>
                    <input
                      type="text"
                      value={editingUser.nome}
                      onChange={(e) => setEditingUser({ ...editingUser, nome: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">Email</label>
                    <input
                      type="email"
                      value={editingUser.email}
                      onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">Setor</label>
                    <select
                      value={editingUser.setor}
                      onChange={(e) => setEditingUser({ ...editingUser, setor: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="Geral">Geral</option>
                      <option value="TI">TI</option>
                      <option value="RH">RH</option>
                      <option value="Comercial">Comercial</option>
                      <option value="Financeiro">Financeiro</option>
                      <option value="Operações">Operações</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-surface mb-2">Perfil</label>
                    <select
                      value={editingUser.role}
                      onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                      className="w-full px-3 py-2 border border-outline-variant rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="colaborador">Colaborador</option>
                      <option value="moderador">Moderador</option>
                      <option value="portaria">Portaria</option>
                      <option value="ti">TI</option>
                      <option value="frota">Frota</option>
                      <option value="sorteio">Sorteio</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setEditingUser(null)}
                      className="flex-1 px-4 py-2 border border-outline-variant text-on-surface rounded-lg hover:bg-surface-container-low transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      Salvar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
